import type { CacheConfig, RequestParameters, Variables } from 'relay-runtime';

import { expValEquals } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

const AGG_BASE_URI = '/gateway/api/graphql';
export const AGG_SLOW_URI: '/gateway/api/graphql/slow' = `${AGG_BASE_URI}/slow`;

/**
 * Returns true when persisted query (PQ) endpoints should be used.
 *
 * When the experiment `confluence_relay_persisted_query_endpoint` is enabled,
 * both SSR and client-side requests use the PQ endpoints (/pq/{id} and /pq/batch).
 *
 * When disabled (default), all requests use the /slow endpoint with full query text.
 *
 * The result is memoised on SSR (where the experiment value cannot change mid-request).
 * On the client the experiment is evaluated fresh each call — no memoisation needed.
 *
 * Not exported — callers must inline expValEquals at their callsite per the
 * confluence-feature-gating/inline-usage lint rule.
 */
let _pqEndpointCached: boolean | undefined;
// eslint-disable-next-line confluence-feature-gating/inline-usage -- SSR memoisation wrapper: evaluates expValEquals once per SSR request to avoid duplicate exposure events. Internal to this module; not a general-purpose re-export.
export const shouldUsePQEndpoint = (): boolean => {
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		if (_pqEndpointCached !== undefined) {
			return _pqEndpointCached;
		}
		_pqEndpointCached = expValEquals(
			'confluence_relay_persisted_query_endpoint',
			'isEnabled',
			true,
		);
		return _pqEndpointCached;
	}
	return expValEquals('confluence_relay_persisted_query_endpoint', 'isEnabled', true);
};

// Exported only for testing — allows resetting the memoised value between tests.
export const __resetPQEndpointCache = (): void => {
	_pqEndpointCached = undefined;
};

const NO_QUERY_TEXT_OPERATION_NAMES_DEFAULT: string[] = [
	'usePrefilteredExtensionList_forge_ui_Query',
	'getForgeUIExtensionsAsync_prefetchedQuery',
	'getForgeUIExtensionsAsync_Query',
	'useExtensionList_forge_ui_Query',
	'queryTypedChildrenContentQuery',
	'creationSettingsQueryQuery',
];

/**
 * Returns true when `request` is for an operation whose generated Relay artifact
 * has no query text. Such operations must never be routed to `/slow` or
 * `/slow/batch`.
 *
 * Prefers the actual `request.text` value when present, and falls back to the
 * Statsig dynamic config allowlist for callsites where `text` is not yet
 * available on the request object.
 */
export const isNoQueryTextOperation = (request: RequestParameters): boolean => {
	if (request.text == null) {
		return true;
	}
	const noQueryTextOperationNames = StatsigConfigurations.getStringList(
		'confluence_relay_no_query_text_operation_names',
		NO_QUERY_TEXT_OPERATION_NAMES_DEFAULT,
	);
	return noQueryTextOperationNames.includes(request.name);
};

/**
 * Returns the AGG endpoint URL for a single request.
 *
 * When PQ experiment is disabled (default):
 * - Uses `/gateway/api/graphql/slow?operation={name}` for all environments.
 *   The caller appends `/batch` via getBatchEndpoint when batching multiple requests.
 *
 * When PQ experiment is enabled, or when the request has no query text:
 * - Uses `/gateway/api/graphql/pq/{id}?operation={name}` when request.id is available.
 * - Falls back to `/gateway/api/graphql?operation={name}` when request.id is null.
 */
export const getAggEndpoint = (
	request: RequestParameters,
	_cacheConfig: CacheConfig = {},
): string => {
	// Operations without query text (e.g. platform-hosted queries that don't enable
	// includeQueryText in their Relay config) cannot use /slow — they must use the
	// persisted-query path even when the PQ experiment is disabled.
	if (!shouldUsePQEndpoint() && !isNoQueryTextOperation(request)) {
		return `${AGG_SLOW_URI}?operation=${request.name}`;
	}

	// PQ experiment enabled or forge operation: use persisted query endpoint
	let endpoint = AGG_BASE_URI;
	if (request.id != null) {
		endpoint = `${endpoint}/pq/${request.id}`;
	}

	return `${endpoint}?operation=${request.name}`;
};

/**
 * Returns the /slow/batch endpoint for batching multiple requests when PQ is disabled.
 * Should only be called when batching 2+ requests together.
 */
export const getBatchEndpoint = (operationNames: string[]): string => {
	return `${AGG_SLOW_URI}/batch?operation=${operationNames.join(',')}`;
};

/**
 * Returns the /pq/batch endpoint for batching multiple persisted-query requests.
 * Should only be called when PQ is enabled and batching 2+ requests together.
 * No query params — the operation names are included in the JSON request body.
 */
export const getPQBatchEndpoint = (): string => {
	return `${AGG_BASE_URI}/pq/batch`;
};

/**
 * Returns the request body for a given GraphQL request.
 *
 * When PQ experiment is disabled (default):
 * - Includes the full query text (required for /slow and /slow/batch endpoints).
 *
 * When PQ experiment is enabled:
 * - Sends only operationName and variables (server resolves query from PQ hash in URL).
 */
export const getRequestBody = (request: RequestParameters, variables: Variables): string => {
	const requestBody: {
		operationName: string;
		variables: Variables;
		query?: string | null | undefined;
	} = {
		operationName: request.name,
		variables,
	};

	if (!shouldUsePQEndpoint() && request.text) {
		requestBody.query = request.text;
	}

	return JSON.stringify(requestBody);
};

/**
 * Returns the PQ batch request body for a set of requests.
 * Each item contains the PQ hash (id), operationName, and variables.
 * Used with the /pq/batch endpoint when PQ experiment is enabled.
 */
export const getPQBatchRequestBody = (
	requests: Array<{ id: string | null; operationName: string; variables: Variables }>,
): string => {
	return JSON.stringify(
		requests.map((req) => ({
			id: req.id,
			operationName: req.operationName,
			variables: req.variables,
		})),
	);
};
